import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fireEvent } from "@/lib/api";
import { VerseBlock } from "@/components/VerseBlock";
import { ArrowRight, Loader2, BookOpen } from "lucide-react";

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

export default function ShareLanding() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [lgUrl, setLgUrl] = useState("");

  useEffect(() => {
    let active = true;
    api.get(`/share/${id}`).then((r) => {
      if (!active) return;
      const anon = !!r.data.anonymous;
      setData(r.data); setState("ok");
      fireEvent(anon ? "anon_share_visit" : "share_visit", id);
      try {
        localStorage.setItem("ipf_ref", id);
        if (anon) localStorage.setItem("ipf_ref_anon", "1"); else localStorage.removeItem("ipf_ref_anon");
      } catch {}
      const title = "Someone thought you might need this wisdom today";
      const desc = "Find biblical wisdom for what you're facing at I Need a Proverb.";
      document.title = `${r.data.headline} · I Need a Proverb`;
      setMeta("description", desc);
      setMeta("og:title", title, "property"); setMeta("og:description", desc, "property");
      setMeta("twitter:title", title); setMeta("twitter:description", desc);
    }).catch(() => { if (active) setState("missing"); });
    api.get("/lifeguide/status").then((r) => { if (active && r.data.url) setLgUrl(r.data.url); }).catch(() => {});
    return () => { active = false; };
  }, [id]);

  const goLifeguide = () => {
    const anon = data?.anonymous;
    fireEvent(anon ? "anon_lifeguide_click" : "lifeguide_click");
    if (lgUrl) window.open(lgUrl, "_blank");
  };

  if (state === "loading") return <div className="py-32 text-center text-[#94A3B8]"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>;
  if (state === "missing") return (
    <div className="max-w-lg mx-auto px-5 py-24 text-center">
      <h1 className="font-serif-display text-3xl text-[#0F172A]">This wisdom link isn't available</h1>
      <p className="text-[#475569] mt-3">The shared link may have expired or is incorrect.</p>
      <Link to="/" className="inline-block mt-6 px-6 py-3 rounded-full bg-[#2C4C3B] text-white font-semibold">Find your own wisdom</Link>
    </div>
  );

  const anon = data.anonymous;
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 text-[#B8912F] mb-3"><BookOpen className="w-5 h-5" strokeWidth={1.6} /></div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#B8912F] mb-2">A gift of wisdom</p>
        {anon ? (
          <>
            <h1 data-testid="landing-anon-heading" className="font-serif-display text-4xl sm:text-5xl text-[#0F172A] leading-tight">Someone thought this wisdom might be helpful to you today.</h1>
            <p className="text-[#475569] mt-3 font-medium">This was shared anonymously.</p>
            <p className="text-[#475569] mt-3 max-w-xl mx-auto italic font-serif-display text-lg">“Sometimes it can be easier to consider wisdom when it isn't attached to an argument, an opinion, or a person.”</p>
          </>
        ) : (
          <>
            <h1 className="font-serif-display text-4xl sm:text-5xl text-[#0F172A] leading-tight">Someone thought you might need this today.</h1>
            {data.headline && <p className="text-lg text-[#475569] mt-3">{data.headline}</p>}
          </>
        )}
      </div>

      <div className="space-y-5">
        {data.verses.map((v, i) => <VerseBlock key={v.reference + i} v={{ ...v, id: v.reference + i }} index={i} />)}
      </div>

      <div className="mt-12 rounded-2xl border border-[#D9E2DA] bg-[#F1F5F1] px-6 py-9 text-center">
        <h2 className="font-serif-display text-3xl text-[#0F172A]">Need wisdom for something <span className="italic">you're</span> facing?</h2>
        <p className="text-[#475569] mt-2">Find biblical wisdom from Proverbs for whatever you're facing right now. Free to use.</p>
        <button data-testid="landing-find-btn" onClick={() => nav("/")}
          className="mt-5 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#2C4C3B] text-white text-base font-semibold hover:bg-[#1E3629] transition-colors">
          Find a Proverb for Me <ArrowRight className="w-5 h-5" />
        </button>
      </div>

      {lgUrl && (
        <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white px-6 py-6 text-center">
          <p className="text-xs uppercase tracking-widest text-[#B8912F] mb-2">Want more help working through this?</p>
          <p className="text-[#475569] leading-relaxed max-w-xl mx-auto">LifeGuide can help you work through what you're facing, one step at a time.</p>
          <button data-testid="landing-lifeguide-btn" onClick={goLifeguide}
            className="mt-4 px-6 py-3 rounded-full border border-[#2C4C3B] text-[#2C4C3B] font-semibold hover:bg-[#F1F5F1] transition-colors">Continue with LifeGuide</button>
        </div>
      )}
    </div>
  );
}
